import React, {
  createContext,
  ReactNode,
  useState,
  useRef,
  useEffect,
} from 'react';

interface PaymentFormContextType {
  renderFormTo: (container: HTMLElement | null) => void;
  initForm: (props: FormProps | null) => void;
  isFormPreloaded: boolean;
  isPaymentSuccessful: boolean;
}

interface FormProps {
  offerHash: string;
  userId: number;
  promocode: string;
  currency: string;
  userEmail: string;
  source: string;
}

export const PaymentFormContext = createContext<PaymentFormContextType>({
  renderFormTo: () => {},
  initForm: () => {},
  isFormPreloaded: false,
  isPaymentSuccessful: false,
});

export const PaymentFormProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [formProps, setFormProps] = useState<FormProps | null>(null);
  const [isRendered, setIsRendered] = useState(false);
  const [targetContainer, setTargetContainer] = useState<HTMLElement | null>(
    null,
  );
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false);

  const formContainerRef = useRef<HTMLDivElement>(null);

  const renderFormTo = (container: HTMLElement | null) => {
    setTargetContainer(container);
  };

  const initForm = (props: FormProps | null) => {
    setIsRendered(false);
    setIsPaymentSuccessful(false);
    setFormProps(props);
    setTargetContainer(null);
  };

  useEffect(() => {
    let apiHandle;

    if (!formProps && formContainerRef.current.hasChildNodes()) {
      formContainerRef.current.firstChild.remove();
    }

    if (formProps && formContainerRef.current) {
      if (typeof window.sm2?.billingUI?.subscribe?.render === 'function') {
        try {
          const newDiv = document.createElement('div');
          formContainerRef.current.appendChild(newDiv);

          apiHandle = window.sm2.billingUI.subscribe
            .render(formContainerRef.current.firstChild, {
              ...formProps,
              onSuccess: () => {
                apiHandle.then((unmount) => {
                  unmount();
                  setIsPaymentSuccessful(true);
                  setIsRendered(false);
                });
              },
            })
            .then((unmount: () => void) => {
              setIsRendered(true);
              return unmount;
            })
            .catch(() => {
              setIsRendered(false);
            });
        } catch {
          setIsRendered(false);
        }
      } else {
        setIsRendered(false);
      }
    }

    return () => {
      apiHandle?.then((unmount) => typeof unmount === 'function' && unmount());
    };
  }, [formProps]);

  useEffect(() => {
    if (!formContainerRef?.current?.firstChild || !isRendered) {
      return;
    }

    if (targetContainer) {
      try {
        targetContainer.appendChild(formContainerRef.current.firstChild);
      } catch (error) {
        console.error('[PaymentFormContext] Form appending error:', error);
      }
    }
  }, [targetContainer, isRendered]);

  return (
    <PaymentFormContext.Provider
      value={{
        renderFormTo,
        initForm,
        isFormPreloaded: isRendered,
        isPaymentSuccessful,
      }}
    >
      {children}

      <div
        ref={formContainerRef}
        data-testid="payment-form-container"
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '500px',
          height: '300px',
          visibility: 'hidden',
          opacity: 0,
          pointerEvents: 'none',
          transition: 'opacity 0.2s',
        }}
      />
    </PaymentFormContext.Provider>
  );
};
